import { TrackedEvent, PropertiesForEvent } from "./tracked-event";
import { clientEnvModule } from "@/modules/client-env";
import { posthogClientManager } from "@/manager/posthog-client";
import posthog from "posthog-js";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { metricsModule } from "@/modules/metrics";
import { BaseError } from "@/domains/errors";
import { segmentAnalyticsInstance } from "@/domains/metrics/segment-analytics-instance";
import { datadogLoggingClientManager } from "@/manager/datadog-logging-client";
import { isNull } from "lodash-es";
import { JsonValue } from "type-fest";
import { getCapturedUtmParams } from "@/app/router/utm-params";

// record when the bundle first executes
const __appStartTime = performance.now();

/**
 * Segment Options type for passing context.
 * We use a simplified type here because Segment's strict TypeScript types
 * require name/source/medium for campaign, but the API accepts partial data.
 */
interface SegmentOptionsWithCampaign {
  context: {
    campaign: Record<string, string>;
  };
}

type TrackedEventPayload = Record<string, JsonValue>;

/**
 * Builds Segment campaign context from captured UTM params.
 * UTM params are captured early (before React mounts) and stored so we can
 * include them here even after they're stripped from the URL.
 *
 * Per Segment docs, the campaign context uses keys without the "utm_" prefix:
 * - utm_source → source, etc.
 *
 * UTM params are included on every Segment call while they're available.
 */
const getSegmentUtmCampaignContext = (): SegmentOptionsWithCampaign | undefined => {
  const utmParams = getCapturedUtmParams();

  if (!utmParams || Object.keys(utmParams).length === 0) {
    return undefined;
  }

  // Build campaign object with utm_ prefix stripped
  const campaign: Record<string, string> = {};

  for (const [key, value] of Object.entries(utmParams)) {
    if (value) {
      // Strip utm_ prefix and map utm_campaign to "name" per Segment spec
      const strippedKey = key.replace(/^utm_/, "");
      const campaignKey = strippedKey === "campaign" ? "name" : strippedKey;
      campaign[campaignKey] = value;
    }
  }

  if (Object.keys(campaign).length === 0) {
    return undefined;
  }

  return { context: { campaign } };
};

const logTrackedEventToDatadog = ({
  event,
  properties,
}: {
  event: TrackedEvent;
  properties: TrackedEventPayload;
}): void => {
  try {
    const datadogLoggingClient = datadogLoggingClientManager.instance();

    datadogLoggingClient.logger.info(`[metrics] ${event}`, {
      analytics_event: event,
      analytics_event_properties: objectModule.safeAsJson(properties),
    });
  } catch {
    // Datadog log mirroring is best-effort and should not affect analytics delivery.
  }
};

interface IdentifyUserInMetricsParams {
  spaceAccountId: string;
  userProperties: {
    email: string;
    name: string;
    createdAt: string | null;
  } & Record<string, unknown>;
  config: {
    isMemmer: boolean;
  };
}

export const identifyUserInMetrics = async ({
  spaceAccountId,
  userProperties,
  config,
}: IdentifyUserInMetricsParams) => {
  logger.debug({
    message: "[metricsModule][identifyUserInMetrics] Identifying user.",
    info: {
      spaceAccountId,
      userProperties: objectModule.safeAsJson({ userProperties }),
      config: objectModule.safeAsJson({ config }),
    },
  });

  try {
    /**
     * Here, we merge anonymous pre-auth activity from a different client into
     * this logged-in user (eg. if the user was on get.mem.ai before and now they
     * are logging into the Desktop app).
     *
     * We check to see if an anonymous ID was passed into the client from another
     * browser used by the same user, and if so, we set it as the current anonymous ID.
     * Otherwise, we allow Segment to manage the anonymous ID automatically.
     *
     * Note that we wrap this in a try/catch - if we fail to set the anonymous ID,
     * we simply won't be able to merge anonymous pre-auth activity into this logged-in
     * user if the user's pre-login activity occurred on a different client (e.g. from
     * browser to desktop).
     */
    try {
      /**
       * This should only be set if:
       * 1. The user is logging in to the Desktop app, AND
       * 2. The user visited get.mem.ai just before logging in.
       */
      const { segmentAnonymousId } = await metricsModule.getSegmentAnonymousId();

      /**
       * We want to effectively carry over the anonymous ID from the browser to
       * the Desktop app.
       */
      if (segmentAnonymousId) {
        await segmentAnalyticsInstance().setAnonymousId(segmentAnonymousId);
      }
    } catch (unknownError) {
      const error = unknownError as Error;

      logger.debug({
        message: "[identifyUserInMetrics] Failed to set anonymous ID.",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    }

    /**
     * If we fail to get the Google Analytics identifiers, we'll just use null.
     */
    const { googleAnalyticsClientId } = await (async () => {
      try {
        return await metricsModule.getGoogleAnalyticsClientId();
      } catch (unknownError) {
        const error = unknownError as Error;

        logger.debug({
          message: "[identifyUserInMetrics] Failed to get Google Analytics client ID.",
          info: { error: objectModule.safeErrorAsJson(error) },
        });

        return { googleAnalyticsClientId: null };
      }
    })();

    const { googleAnalyticsSessionId } = await (async () => {
      try {
        return await metricsModule.getGoogleAnalyticsSessionId();
      } catch (unknownError) {
        const error = unknownError as Error;

        logger.debug({
          message: "[identifyUserInMetrics] Failed to get Google Analytics session ID.",
          info: { error: objectModule.safeErrorAsJson(error) },
        });

        return { googleAnalyticsSessionId: null };
      }
    })();

    /**
     * This is the important call, which ensures that all tracked events are associated with
     * the correct user.
     */
    // Include UTM campaign context for Segment
    // Note: We cast to `any` because Segment's TS types are stricter than their API
    // (they require name/source/medium but the API accepts partial campaign data)
    const segmentUtmContext = getSegmentUtmCampaignContext();
    await segmentAnalyticsInstance().identify(
      spaceAccountId,
      {
        ...userProperties,
        gaClientId: googleAnalyticsClientId,
        gaSessionId: googleAnalyticsSessionId,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      segmentUtmContext as any
    );

    // Also do posthog directly
    posthogClientManager.initialize({
      disableSessionRecording: config.isMemmer,
    });

    posthogClientManager.identify(spaceAccountId, {
      email: userProperties.email,
      name: userProperties.name,
    });

    // Set UTM params as person properties for attribution in PostHog
    // Uses $set_once so these are only set on first identification
    const posthogUtmParams = getCapturedUtmParams();
    if (posthogUtmParams && Object.keys(posthogUtmParams).length > 0) {
      const initialUtmProperties: Record<string, string | undefined> = {};
      for (const [key, value] of Object.entries(posthogUtmParams)) {
        // Prefix with "initial_" for $set_once properties (keep utm_ prefix for PostHog)
        initialUtmProperties[`initial_${key}`] = value;
      }
      posthogClientManager.setPersonProperties({}, initialUtmProperties);
    }
  } catch (unknownError) {
    const error = unknownError as BaseError;

    logger.debug({
      message: "[identifyUserInMetrics] Failed to identify user.",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
  }
};

/**
 * `trackEvent` fires off a segment event in-the-background.
 *
 * Type-safe: properties are validated based on the event type.
 * Events with specific property types (like ExperimentStarted) require matching properties.
 * Other events accept generic Record<string, unknown> properties.
 */
export function trackEvent<T extends TrackedEvent>(
  event: T,
  properties?: PropertiesForEvent<T>
): void {
  /**
   * We don't await this because we don't want to block the main thread.
   */
  void trackEventAsync(event, properties);
}

/**
 * A version of trackEvent that is awaitable, because it relies on some
 * async reads when collecting properties for the tracked event.
 */
async function trackEventAsync<T extends TrackedEvent>(
  event: T,
  properties?: PropertiesForEvent<T>
): Promise<void> {
  try {
    const [
      { googleAnalyticsClientId },
      { googleAnalyticsSessionId },
      { metadataString },
      { electronAppInfo },
    ] = await Promise.all([
      metricsModule.getGoogleAnalyticsClientId(),
      metricsModule.getGoogleAnalyticsSessionId(),
      metricsModule.getMetadataString(),
      metricsModule.getElectronAppInfo(),
    ]);

    const additionalProperties: Record<string, JsonValue> = {};

    if (!isNull(electronAppInfo)) {
      additionalProperties.electronSystemArchitecture = electronAppInfo.electronSystemArchitecture;
      additionalProperties.electronAppVersion = electronAppInfo.electronAppVersion;
      additionalProperties.electronRuntimeVersion = electronAppInfo.electronRuntimeVersion;
    }

    const mergedProperties = {
      nodeEnv: clientEnvModule.nodeEnv(),
      isProd: clientEnvModule.isProd(),
      isDev: clientEnvModule.isDev(),
      isTest: clientEnvModule.isTest(),
      route_path: location.pathname,
      localBackendApiIsEnabled: clientEnvModule.localBackendApiIsEnabled(),
      clientId: clientEnvModule.clientId(),
      clientGroupId: clientEnvModule.clientGroupId(),
      buildInfo: clientEnvModule.buildInfo(),
      packageVersion: clientEnvModule.packageVersion(),
      isDesktop: clientEnvModule.isDesktop(),
      navigatorPlatform: navigator.platform, // this is technically deprecated, but it's still the best way to get the OS
      $session_id: posthog.get_session_id(),
      gaClientId: googleAnalyticsClientId,
      gaSessionId: googleAnalyticsSessionId,
      metricsMetadataString: metadataString,
      ...additionalProperties,
      ...properties,
    };

    const sanitizedProperties = objectModule.safeAsJson(mergedProperties) as TrackedEventPayload;

    logTrackedEventToDatadog({
      event,
      properties: sanitizedProperties,
    });

    // Include UTM campaign context if available
    // Note: We cast to `any` because Segment's TS types are stricter than their API
    const utmContext = getSegmentUtmCampaignContext();
    await segmentAnalyticsInstance().track(
      event,
      mergedProperties,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      utmContext as any
    );
  } catch (unknownError) {
    const error = unknownError as BaseError;

    logger.debug({
      message: "[trackEventAsync] Failed to track event.",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
  }
}

let __didCapturePerformanceAppBoot = false;

export const capturePerformanceAppBoot = (details: Record<string, unknown>) => {
  // only do this once
  if (__didCapturePerformanceAppBoot) return;
  __didCapturePerformanceAppBoot = true;

  /**
   * We don't await this because we don't want to block the main thread.
   */
  void capturePerformanceAppBootAsync(details);
};

export const capturePerformanceAppBootAsync = async (details: Record<string, unknown>) => {
  try {
    const now = performance.now();

    const [
      { googleAnalyticsClientId },
      { googleAnalyticsSessionId },
      { metadataString },
      { electronAppInfo },
    ] = await Promise.all([
      metricsModule.getGoogleAnalyticsClientId(),
      metricsModule.getGoogleAnalyticsSessionId(),
      metricsModule.getMetadataString(),
      metricsModule.getElectronAppInfo(),
    ]);

    const additionalProperties: Record<string, JsonValue> = {};

    if (!isNull(electronAppInfo)) {
      additionalProperties.electronSystemArchitecture = electronAppInfo.electronSystemArchitecture;
      additionalProperties.electronAppVersion = electronAppInfo.electronAppVersion;
      additionalProperties.electronRuntimeVersion = electronAppInfo.electronRuntimeVersion;
    }

    /**
     * Note that we explicitly don't await this.
     */
    // Include UTM campaign context if available
    // Note: We cast to `any` because Segment's TS types are stricter than their API
    const utmContext = getSegmentUtmCampaignContext();
    await segmentAnalyticsInstance().track(
      "performance_app_boot",
      {
        seconds: (now - __appStartTime) / 1000,
        gaClientId: googleAnalyticsClientId,
        gaSessionId: googleAnalyticsSessionId,
        metricsMetadata: metadataString,
        ...additionalProperties,
        ...details,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      utmContext as any
    );
  } catch (unknownError) {
    const error = unknownError as BaseError;

    logger.debug({
      message: "[capturePerformanceAppBoot] Failed to track event.",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
  }
};
